import { useLocation, Link } from 'react-router-dom'
import { Bell, Search, ChevronRight, LayoutDashboard } from 'lucide-react'
import { ROUTES } from '@/constants'
import { useAuthStore } from '@/store/authStore'
import { useState, useEffect } from 'react'
import { notificationService } from '@/services/firebase/notifications'

const ROUTE_LABELS: Record<string, string> = {
  [ROUTES.DASHBOARD]:     'Dashboard',
  [ROUTES.TASKS]:         'Tasks',
  [ROUTES.NEW_TASK]:      'New Task',
  [ROUTES.INQUIRIES]:     'Enquiries',
  [ROUTES.NEW_INQUIRY]:   'New Enquiry',
  [ROUTES.INSTALLATIONS]: 'Installations',
  [ROUTES.TICKETS]:       'Tickets',
  [ROUTES.NEW_TICKET]:    'New Ticket',
  [ROUTES.FOLLOWUPS]:     'Follow-ups',
  [ROUTES.ENGINEERS]:     'Users',
  [ROUTES.CUSTOMERS]:          'Customers',
  [ROUTES.NETWORK_TOPOLOGY]:   'Network Topology',
  [ROUTES.REPORTS]:       'Reports',
  [ROUTES.SETTINGS]:      'Settings',
  [ROUTES.PROFILE]:       'Profile',
  [ROUTES.NOTIFICATIONS]: 'Notifications',
}

const getLabel = (path: string) => {
  if (ROUTE_LABELS[path]) return ROUTE_LABELS[path]
  for (const [route, label] of Object.entries(ROUTE_LABELS)) {
    const pattern = route.replace(/:[^/]+/g, '[^/]+')
    if (new RegExp(`^${pattern}$`).test(path)) return label
  }
  return 'BSNL FTTH'
}

const Topbar = () => {
  const location = useLocation()
  const { user } = useAuthStore()
  const isHome = location.pathname === ROUTES.DASHBOARD
  const currentLabel = getLabel(location.pathname)
  const [scrolled, setScrolled] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)

  useEffect(() => {
    const el = document.querySelector('main')
    if (!el) return
    const handler = () => setScrolled(el.scrollTop > 8)
    el.addEventListener('scroll', handler, { passive: true })
    return () => el.removeEventListener('scroll', handler)
  }, [])

  useEffect(() => {
    if (!user?.uid) return
    const unsub = notificationService.subscribeToNotifications(user.uid, items => {
      setUnreadCount(items.filter(n => !n.read).length)
    })
    return () => unsub()
  }, [user?.uid])

  return (
    <header className="hidden lg:flex items-center justify-between h-16 px-6 shrink-0 sticky top-0 z-30 transition-all duration-300"
      style={{
        background: 'rgba(240, 244, 250, 0.9)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
        boxShadow: scrolled ? '0 4px 16px rgba(0,0,0,0.06)' : 'none',
      }}>

      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm">
        {!isHome ? (
          <>
            <Link to={ROUTES.DASHBOARD}
              className="flex items-center gap-1.5 text-slate-400 hover:text-slate-700 transition-colors">
              <LayoutDashboard size={13} />
              <span className="text-xs font-medium">Dashboard</span>
            </Link>
            <ChevronRight size={12} className="text-slate-300" />
            <span className="font-semibold text-slate-800 text-sm">{currentLabel}</span>
          </>
        ) : (
          <div>
            <p className="font-bold text-slate-800 text-base">Dashboard</p>
            <p className="text-[11px] text-slate-400 -mt-0.5">
              {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
          </div>
        )}
      </div>

      {/* Right controls */}
      <div className="flex items-center gap-2.5">
        {/* Search */}
        <div className="flex items-center gap-2 bg-white/80 border border-slate-200/80 rounded-xl px-3 py-2 w-52 focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-400 transition-all shadow-sm"
          style={{ backdropFilter: 'blur(8px)' }}>
          <Search size={13} className="text-slate-400 shrink-0" />
          <input
            placeholder="Search…"
            className="flex-1 text-sm bg-transparent outline-none text-slate-700 placeholder-slate-400 min-w-0"
          />
          <kbd className="hidden sm:block text-[10px] text-slate-300 font-mono bg-slate-100 px-1.5 py-0.5 rounded font-medium">⌘K</kbd>
        </div>

        {/* Notifications bell */}
        <Link to={ROUTES.NOTIFICATIONS}
          className="relative w-9 h-9 flex items-center justify-center rounded-xl bg-white/80 border border-slate-200/80 shadow-sm hover:bg-white hover:border-slate-300 hover:shadow-md transition-all"
          style={{ backdropFilter: 'blur(8px)' }}>
          <Bell size={15} className="text-slate-600" />
          {unreadCount > 0 && (
            <span className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-orange-500" />
          )}
        </Link>

        {/* Divider */}
        <div className="w-px h-6 bg-slate-200/60" />

        {/* User chip */}
        <Link to={ROUTES.PROFILE}
          className="flex items-center gap-2.5 bg-white/80 border border-slate-200/80 rounded-xl pl-1.5 pr-3 py-1.5 shadow-sm hover:bg-white hover:shadow-md hover:border-slate-300 transition-all"
          style={{ backdropFilter: 'blur(8px)' }}>
          <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-black shrink-0"
            style={{ background: 'linear-gradient(135deg, #6366f1 0%, #3b82f6 100%)' }}>
            {user?.name?.charAt(0)?.toUpperCase() ?? 'U'}
          </div>
          <div className="min-w-0">
            <p className="text-xs font-bold text-slate-800 truncate max-w-[100px] leading-tight">{user?.name ?? 'User'}</p>
            <p className="text-[9px] text-slate-400 font-medium leading-tight mt-0.5">
              {user?.role === 'admin' ? 'Administrator' : 'Engineer'}
            </p>
          </div>
        </Link>
      </div>
    </header>
  )
}

export default Topbar
